import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "../components/ui/button";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetClose,
    SheetTitle,
    SheetDescription,
} from "../components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const links = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/packages", label: "Packages" },
    { to: "/shop", label: "Shop" },
    { to: "/corporate", label: "Corporate" },
    { to: "/blog", label: "Insights" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    return (
        <header
            className="ssh-nav fixed top-0 left-0 right-0 z-50"
            data-testid="site-navbar"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
                <Link
                    to="/"
                    className="flex items-baseline gap-2 group"
                    data-testid="brand-link"
                >
                    <span className="font-serif text-2xl tracking-tight text-[#2C2B29]">
                        Soul Sync
                    </span>
                    <span className="font-serif text-2xl italic text-[#A68A6B] tracking-tight">
                        Healing
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            className={({ isActive }) =>
                                `text-sm tracking-wide transition-colors ${
                                    isActive
                                        ? "text-[#A68A6B]"
                                        : "text-[#2C2B29] hover:text-[#A68A6B]"
                                }`
                            }
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:block">
                    <Link to="/contact#booking">
                        <Button
                            className="ssh-btn-primary rounded-none px-6 py-5 text-xs uppercase tracking-[0.2em] hover:bg-[#897157]"
                            data-testid="nav-book-session"
                        >
                            Book a Session
                        </Button>
                    </Link>
                </div>

                {/* Mobile menu */}
                <div className="lg:hidden">
                    <Sheet>
                        <SheetTrigger asChild>
                            <button
                                aria-label="Open menu"
                                className="p-2 -mr-2 text-[#2C2B29]"
                                data-testid="mobile-menu-trigger"
                            >
                                <Menu className="h-6 w-6" strokeWidth={1.5} />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            className="bg-[#FAF8F5] border-l border-[#EFEBE4] w-[88vw] sm:w-96"
                        >
                            <VisuallyHidden>
                                <SheetTitle>Site Navigation</SheetTitle>
                                <SheetDescription>
                                    Mobile navigation menu for Soul Sync
                                    Healing
                                </SheetDescription>
                            </VisuallyHidden>
                            <div className="mt-10 flex flex-col gap-6">
                                {links.map((l) => (
                                    <SheetClose asChild key={l.to}>
                                        <NavLink
                                            to={l.to}
                                            end={l.to === "/"}
                                            className="font-serif text-3xl tracking-tight text-[#2C2B29] hover:text-[#A68A6B] transition-colors"
                                            data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                                        >
                                            {l.label}
                                        </NavLink>
                                    </SheetClose>
                                ))}
                                <div className="pt-6 border-t border-[#EFEBE4]">
                                    <SheetClose asChild>
                                        <Link to="/contact#booking">
                                            <Button
                                                className="w-full ssh-btn-primary rounded-none py-6 text-xs uppercase tracking-[0.2em]"
                                                data-testid="mobile-book-session"
                                            >
                                                Book a Session
                                            </Button>
                                        </Link>
                                    </SheetClose>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
